"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  type Option,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { MannWhitneyResults } from "@/components/tools/stats-tests/mann-whitney-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import { mannWhitney } from "@/lib/tool-kit/stats-tests/mann-whitney";

/** English strings. A translated content file overrides these through `ui`. */
export const MANN_WHITNEY_U_TEST_CALCULATOR_STRINGS = {
  sample1Label: "Sample 1 — numbers separated by commas, spaces, or new lines",
  sample2Label: "Sample 2 — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "3.2, 4.1, 2.8, 5.0",
  valuesDetected: "one: value|other: values",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: the distributions differ",
  less: "Sample 1 tends to be smaller",
  greater: "Sample 1 tends to be larger",
  alphaLabel: "Significance level (α)",
  continuity: "Continuity correction (normal approximation)",
  tryExample: "Try an example",
  example1: "3.2, 4.1, 2.8, 5.0, 3.9, 4.4, 3.5, 2.6",
  example2: "4.8, 5.6, 4.3, 6.1, 5.2, 3.7, 5.9, 4.9, 6.4",
  results: "Results",
  uStatistic: "U (the smaller of U₁ and U₂)",
  pExact: "p-value (exact)",
  pNormal: "p-value (normal approximation)",
  zScore: "z",
  effectR: "Effect size r = |z| / √N",
  rankBiserial: "Rank-biserial correlation",
  meanRanks: "Mean ranks (1, 2)",
  medians: "Medians (1, 2)",
  methodExact:
    "Exact p-value: there are no ties and both samples have 20 or fewer values, so p comes from the exact distribution of U. The normal approximation gives {p} for comparison.",
  methodTies:
    "Normal approximation with a tie correction: the data contain ties, so the exact distribution does not apply.",
  methodTiesCc:
    "Normal approximation with a tie correction and a continuity correction: the data contain ties, so the exact distribution does not apply.",
  methodSize:
    "Normal approximation with a tie correction: a sample has more than 20 values, where the approximation is accurate.",
  methodSizeCc:
    "Normal approximation with tie and continuity corrections: a sample has more than 20 values, where the approximation is accurate.",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the two distributions differ.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the distributions differ.",
  superiority:
    "Sample 1 is larger in {percent} of the {pairs} possible pairs (ties count half), so the rank-biserial correlation is {rrb}: positive when sample 1 tends to be larger, negative when smaller.",
  ranksHeading: "Combined ranking",
  ranksHint: "Tied values share the average of the ranks they span.",
  value: "Value",
  sample: "Sample",
  rank: "Rank",
  ranksHidden: "The ranking table is shown for up to 40 values in total.",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "*U* = {u}, *z* = {z}, *p* {p}, *r* = {r}",
  apaNoteExact:
    "p is exact. Give each group's median (or mean rank) alongside the test.",
  apaNoteNormal:
    "p is from the normal approximation. Give each group's median (or mean rank) alongside the test.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter at least two numbers in each sample.",
  errorConstant:
    "Every value is identical, so the ranks carry no information and U cannot be tested.",
} as const;

export default function MannWhitneyUTestCalculatorWidget() {
  const strings = useToolUi(MANN_WHITNEY_U_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [data1, setData1] = useState("");
  const [data2, setData2] = useState("");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [continuity, setContinuity] = useState(true);

  const x = parseNumberList(data1);
  const y = parseNumberList(data2);
  const enough = x.length >= 2 && y.length >= 2;
  const result = enough ? mannWhitney(x, y, { continuity, alternative }) : null;
  const count = (n: number) =>
    `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextAreaField
          hint={count(x.length)}
          id="mwu-data-1"
          label={strings.sample1Label}
          onChange={setData1}
          placeholder={strings.dataPlaceholder}
          value={data1}
        />
        <TextAreaField
          hint={count(y.length)}
          id="mwu-data-2"
          label={strings.sample2Label}
          onChange={setData2}
          placeholder={strings.dataPlaceholder}
          value={data2}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="mwu-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="mwu-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
      </div>
      <Checkbox
        checked={continuity}
        label={strings.continuity}
        onChange={setContinuity}
      />
      <ExampleButton
        label={strings.tryExample}
        onClick={() => {
          setData1(strings.example1);
          setData2(strings.example2);
        }}
      />
      {result ? (
        <MannWhitneyResults
          alpha={Number(alpha)}
          continuity={continuity}
          fmt={fmt}
          r={result}
          strings={strings}
          x={x}
          y={y}
        />
      ) : (
        <Notice>{enough ? strings.errorConstant : strings.prompt}</Notice>
      )}
    </div>
  );
}
